import { Outlet, Link, useLocation } from 'react-router-dom';
import { useExamAuth } from '@/hooks/useExamAuth';
import { useQuery } from '@tanstack/react-query';
import {
  LayoutDashboard,
  Users,
  Building2,
  BarChart3,
  Settings,
  LogOut,
  Menu,
  X,
  Search,
  ChevronDown,
  Shield,
  Megaphone,
  CreditCard,
  Activity,
  Check,
  Database,
  HeartPulse,
} from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { NotificationBell } from '@/components/notifications/NotificationBell';
import { tenantsApi } from '@/api/endpoints';
import { Tenant } from '@/types';

// Tenant context for system admin to view data as a specific tenant
interface TenantContext {
  selectedTenant: Tenant | null;
  setSelectedTenant: (tenant: Tenant | null) => void;
}

// Create a simple context for tenant selection
import { createContext, useContext } from 'react';

export const TenantSelectorContext = createContext<TenantContext>({
  selectedTenant: null,
  setSelectedTenant: () => {},
});

export const useTenantSelector = () => useContext(TenantSelectorContext);

// Tenant Selector Dropdown Component
const TenantSelector = () => {
  const { selectedTenant, setSelectedTenant } = useTenantSelector();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fetch tenants
  const { data: tenantsResponse, isLoading } = useQuery({
    queryKey: ['tenants-selector'],
    queryFn: () => tenantsApi.list(),
  });

  const tenants = tenantsResponse?.data?.data || [];

  // Filter tenants by search
  const filteredTenants = tenants.filter((tenant: Tenant) =>
    tenant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tenant.domain.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-3 py-2 rounded-lg bg-white/[0.03] border border-white/[0.08] hover:bg-white/[0.06] hover:border-white/[0.12] transition-all"
      >
        <Building2 className="w-4 h-4 text-teal-400" />
        <span className="text-sm text-white/80 max-w-[180px] truncate">
          {selectedTenant ? selectedTenant.name : 'All Tenants (Platform View)'}
        </span>
        <ChevronDown className={`w-4 h-4 text-white/40 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-80 bg-[#1a1f2e] border border-white/[0.08] rounded-xl shadow-xl z-50 overflow-hidden">
          {/* Search */}
          <div className="p-3 border-b border-white/[0.06]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/40" />
              <input
                type="text"
                placeholder="Search tenants..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2 text-sm bg-white/[0.03] border border-white/[0.08] rounded-lg text-white/90 placeholder:text-white/40 focus:outline-none focus:border-teal-500/50"
              />
            </div>
          </div>

          {/* Platform View option */}
          <div className="p-2 border-b border-white/[0.06]">
            <button
              onClick={() => {
                setSelectedTenant(null);
                setIsOpen(false);
                setSearchTerm('');
              }}
              className={`w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-all ${
                !selectedTenant
                  ? 'bg-teal-500/20 border border-teal-500/30'
                  : 'hover:bg-white/[0.04]'
              }`}
            >
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/30 flex items-center justify-center">
                <Shield className="w-4 h-4 text-purple-400" />
              </div>
              <div className="flex-1 text-left">
                <p className="text-sm font-medium text-white/90">All Tenants</p>
                <p className="text-xs text-white/50">Platform-wide view</p>
              </div>
              {!selectedTenant && <Check className="w-4 h-4 text-teal-400" />}
            </button>
          </div>

          {/* Tenant list */}
          <div className="max-h-64 overflow-y-auto custom-scrollbar">
            {isLoading ? (
              <div className="p-4 text-center">
                <div className="spinner mx-auto mb-2"></div>
                <p className="text-sm text-white/50">Loading tenants...</p>
              </div>
            ) : filteredTenants.length > 0 ? (
              <div className="p-2 space-y-1">
                {filteredTenants.map((tenant: Tenant) => (
                  <button
                    key={tenant.id}
                    onClick={() => {
                      setSelectedTenant(tenant);
                      setIsOpen(false);
                      setSearchTerm('');
                    }}
                    className={`w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-all ${
                      selectedTenant?.id === tenant.id
                        ? 'bg-teal-500/20 border border-teal-500/30'
                        : 'hover:bg-white/[0.04]'
                    }`}
                  >
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-500/20 to-cyan-500/20 border border-teal-500/30 flex items-center justify-center">
                      <Building2 className="w-4 h-4 text-teal-400" />
                    </div>
                    <div className="flex-1 text-left min-w-0">
                      <p className="text-sm font-medium text-white/90 truncate">{tenant.name}</p>
                      <p className="text-xs text-white/50 truncate">{tenant.domain}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-0.5 text-xs rounded-full ${
                        tenant.is_active
                          ? 'bg-emerald-500/20 text-emerald-400'
                          : 'bg-red-500/20 text-red-400'
                      }`}>
                        {tenant.is_active ? 'Active' : 'Inactive'}
                      </span>
                      {selectedTenant?.id === tenant.id && <Check className="w-4 h-4 text-teal-400" />}
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="p-4 text-center">
                <p className="text-sm text-white/50">No tenants found</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-2 border-t border-white/[0.06]">
            <Link
              to="/system-admin/tenants"
              onClick={() => setIsOpen(false)}
              className="flex items-center justify-center space-x-2 px-3 py-2 text-sm text-teal-400 hover:bg-teal-500/10 rounded-lg transition-colors"
            >
              <Settings className="w-4 h-4" />
              <span>Manage Tenants</span>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export const SystemAdminLayout = () => {
  const { user, logout } = useExamAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);

  const handleLogout = async () => {
    await logout();
  };

  // System Admin navigation
  const navigation = [
    { name: 'Dashboard', href: '/system-admin/dashboard', icon: LayoutDashboard },
    { name: 'Tenants', href: '/system-admin/tenants', icon: Building2 },
    { name: 'Users', href: '/system-admin/users', icon: Users },
    { name: 'Analytics', href: '/system-admin/analytics', icon: BarChart3 },
    { name: 'Announcements', href: '/system-admin/announcements', icon: Megaphone },
    { name: 'Activity Logs', href: '/system-admin/activity', icon: Activity },
    { name: 'Billing', href: '/system-admin/billing', icon: CreditCard },
    { name: 'Backups', href: '/system-admin/backups', icon: Database },
    { name: 'System Health', href: '/system-admin/system-health', icon: HeartPulse },
    { name: 'Settings', href: '/system-admin/settings', icon: Settings },
  ];

  return (
    <TenantSelectorContext.Provider value={{ selectedTenant, setSelectedTenant }}>
      <div className="min-h-screen">
        {/* Mobile sidebar backdrop */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-20 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        <aside
          className={`fixed inset-y-0 left-0 w-56 sidebar-dark transform transition-transform duration-300 ease-in-out z-30 lg:translate-x-0 ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <div className="h-full flex flex-col">
            {/* Logo */}
            <div className="h-16 flex items-center justify-between px-4 border-b border-white/[0.06]">
              <Link to="/system-admin/dashboard" className="flex items-center space-x-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                  <Shield className="w-4 h-4 text-white" />
                </div>
                <div>
                  <span className="text-sm font-semibold text-white/90">System Admin</span>
                  <p className="text-[10px] text-white/40">Platform Control</p>
                </div>
              </Link>
              <button
                onClick={() => setSidebarOpen(false)}
                className="lg:hidden text-white/50 hover:text-white/80 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* User info */}
            <div className="px-4 py-4 border-b border-white/[0.06]">
              <div className="flex items-center space-x-3">
                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/30 flex items-center justify-center">
                  <span className="text-purple-400 font-medium text-sm">
                    {user?.first_name?.[0]}{user?.last_name?.[0]}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white/90 truncate">
                    {user?.first_name} {user?.last_name}
                  </p>
                  <p className="text-xs text-purple-400">System Administrator</p>
                </div>
              </div>
            </div>

            {/* Navigation */}
            <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto custom-scrollbar" data-lenis-prevent>
              {navigation.map((item) => {
                const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/');
                const Icon = item.icon;

                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-all ${
                      isActive
                        ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                        : 'text-white/60 hover:text-white/90 hover:bg-white/[0.04]'
                    }`}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="text-sm">{item.name}</span>
                  </Link>
                );
              })}
            </nav>

            {/* Switch to Tenant View */}
            <div className="p-3 border-t border-white/[0.06]">
              <Link
                to="/admin/dashboard"
                className="w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg text-white/60 hover:text-white/90 hover:bg-white/[0.04] transition-all"
              >
                <LayoutDashboard className="w-4 h-4" />
                <span className="text-sm">Tenant Admin View</span>
              </Link>
            </div>

            {/* Logout button */}
            <div className="p-3 border-t border-white/[0.06]">
              <button
                onClick={handleLogout}
                className="w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg text-white/60 hover:text-red-400 hover:bg-red-500/10 transition-all"
              >
                <LogOut className="w-4 h-4" />
                <span className="text-sm">Logout</span>
              </button>
            </div>
          </div>
        </aside>

        {/* Main content */}
        <div className="lg:ml-56">
          {/* Top bar */}
          <header className="h-16 header-dark sticky top-0 z-10 flex items-center justify-between px-4 lg:px-6">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden text-white/60 hover:text-white/90 transition-colors"
              >
                <Menu className="w-5 h-5" />
              </button>

              {/* Tenant Selector */}
              <TenantSelector />
            </div>

            <div className="flex items-center space-x-3">
              {/* Search */}
              <div className="hidden md:flex items-center">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/40" />
                  <input
                    type="text"
                    placeholder="Search..."
                    className="input-dark pl-9 pr-4 py-1.5 text-sm w-48 lg:w-64"
                  />
                </div>
              </div>

              {/* Notifications */}
              <NotificationBell />

              {/* User menu */}
              <div className="flex items-center space-x-2 pl-3 border-l border-white/[0.06]">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/30 flex items-center justify-center">
                  <span className="text-purple-400 font-medium text-xs">
                    {user?.first_name?.[0]}{user?.last_name?.[0]}
                  </span>
                </div>
                <div className="hidden lg:block">
                  <p className="text-sm font-medium text-white/90">{user?.first_name}</p>
                </div>
                <ChevronDown className="w-4 h-4 text-white/40" />
              </div>
            </div>
          </header>

          {/* Selected tenant indicator */}
          {selectedTenant && (
            <div className="bg-teal-500/10 border-b border-teal-500/20 px-4 lg:px-6 py-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Building2 className="w-4 h-4 text-teal-400" />
                  <span className="text-sm text-teal-400">
                    Viewing as: <strong>{selectedTenant.name}</strong>
                  </span>
                </div>
                <button
                  onClick={() => setSelectedTenant(null)}
                  className="text-xs text-teal-400 hover:text-teal-300 underline"
                >
                  Clear selection
                </button>
              </div>
            </div>
          )}

          {/* Page content */}
          <main className="p-4 lg:p-6">
            <Outlet />
          </main>
        </div>
      </div>
    </TenantSelectorContext.Provider>
  );
};
